import {
  Asset, DEFAULT_PRICING, isEveOfHoliday, isHolidayDate,
  minHoursForSpace, ScheduleException, SeasonalBlock,
} from '../../models/asset.model';
import { Booking, isCalendarBlockingStatus } from '../../models/booking.model';
import {
  dateFromDateStr, dayHoursForDate, dayOperatingWindowForDate,
  logicalScheduleDay, weekdayIndexMon0,
} from './schedule-pricing.utils';
import { buildVisibleSlots, findSlotIndex, GridSlot, slotFractions } from './slot-grid.utils';
import { parseCloseHour, parseHour } from './schedule.utils';

export type OccupancyCellMeta = {
  label: string;
  status: string;
  /** Set on recurring blocks that can be skipped for full-day bookings. */
  cancelableOnFullDay?: boolean;
};

/** How domingo/festivo blocks affect a holiday date (wizard Calendario preview). */
export type FestivoBlockPreview =
  | { kind: 'not-holiday' }
  | { kind: 'closed' }
  | { kind: 'open-no-blocks' }
  | { kind: 'hard-blocks' }
  | { kind: 'cancelable-blocks'; fullDayEligible: boolean };

/** True when a one-time exception releases this half-hour on `dateStr`. */
export function isSlotReleasedByException(
  dateStr: string,
  cellTime: number,
  exceptions: ScheduleException[],
): boolean {
  return exceptions.some(ex =>
    ex.date === dateStr
    && parseHour(ex.startTime) <= cellTime
    && cellTime < parseHour(ex.endTime),
  );
}

/**
 * Seasonal blocks that apply to a concrete calendar day.
 *
 * Recurring patterns use `logicalScheduleDay` (festivos → 6, vísperas → 5).
 * Single-day seasonal entries also match the calendar weekday so a one-off
 * block created for "Miércoles 2026-05-20" still applies when that date is
 * later marked as festivo (logical day 6).
 */
export function seasonalBlocksForDay(
  space: Asset,
  date: Date,
  dateStr: string,
): SeasonalBlock[] {
  const logicalDay  = logicalScheduleDay(date, dateStr, space);
  const calendarDay = weekdayIndexMon0(dateStr);
  const remappedDay = isHolidayDate(dateStr, space) || isEveOfHoliday(dateStr, space);
  return (space.seasonalBlocks ?? []).filter(sb => {
    if (dateStr < sb.startDate || dateStr > sb.endDate) return false;
    if (sb.day === logicalDay) return true;
    // Festivos/vísperas solo usan el día lógico (domingo/festivo o sábado/víspera).
    if (remappedDay) return false;
    return sb.startDate === sb.endDate && sb.day === calendarDay;
  });
}

/** Marks a half-hour range as blocked (fixed forever or seasonal window). */
export function markRecurringBlockRange(params: {
  slots: GridSlot[];
  slotFractions: number[];
  open: number;
  close: number;
  startTime: string;
  endTime: string;
  label: string;
  status: string;
  dateStr: string;
  exceptions: ScheduleException[];
  freeMask: boolean[];
  busyMeta: (OccupancyCellMeta | null)[];
  onlyIfFree?: boolean;
  anonymize?: boolean;
  /** When true, also sets cancelableMask[i] = true so full-day bookings can skip this block. */
  cancelableOnFullDay?: boolean;
  cancelableMask?: boolean[];
}): void {
  const {
    slots, slotFractions: fracs, open, close,
    startTime, endTime, label, status, dateStr, exceptions,
    freeMask, busyMeta, onlyIfFree = false, anonymize = false,
    cancelableOnFullDay = false, cancelableMask,
  } = params;

  const startFrac = Math.max(open, Math.min(close, parseHour(startTime)));
  const endFrac = Math.max(open, Math.min(close, parseHour(endTime)));
  if (endFrac <= startFrac) return;

  const start = Math.max(0, findSlotIndex(slots, startFrac));
  let end = findSlotIndex(slots, endFrac);
  if (end === -1) end = slots.length;

  for (let i = start; i < end; i++) {
    if (onlyIfFree && !freeMask[i]) continue;
    const frac = fracs[i];
    if (frac < open || frac >= close) continue;
    if (isSlotReleasedByException(dateStr, frac, exceptions)) continue;
    freeMask[i] = false;
    busyMeta[i] = anonymize
      ? { label: '', status: 'OCCUPIED' }
      : {
          label: label || 'No disponible',
          status,
          ...(cancelableOnFullDay ? { cancelableOnFullDay: true } : {}),
        };
    if (cancelableOnFullDay && cancelableMask) {
      cancelableMask[i] = true;
    }
  }
}

/** Applies forever fixed blocks (per-slot) for one day. */
export function applyFixedBlocksToDay(params: {
  space: Asset;
  date: Date;
  dateStr: string;
  slots: GridSlot[];
  slotFractions: number[];
  open: number;
  close: number;
  exceptions: ScheduleException[];
  freeMask: boolean[];
  busyMeta: (OccupancyCellMeta | null)[];
  cancelableMask?: boolean[];
  anonymize?: boolean;
}): void {
  const logicalDay = logicalScheduleDay(params.date, params.dateStr, params.space);
  for (const fb of params.space.fixedBlocks ?? []) {
    if (fb.day !== logicalDay) continue;
    const idx = params.slots.findIndex(s => s.hour === fb.hour && s.half === fb.half);
    if (idx === -1) continue;
    const frac = params.slotFractions[idx];
    if (frac < params.open || frac >= params.close) continue;
    if (isSlotReleasedByException(params.dateStr, frac, params.exceptions)) continue;
    params.freeMask[idx] = false;
    params.busyMeta[idx] = params.anonymize
      ? { label: '', status: 'OCCUPIED' }
      : {
          label: fb.label || 'No disponible',
          status: 'FIXED',
          ...(fb.cancelableOnFullDay ? { cancelableOnFullDay: true } : {}),
        };
    if (fb.cancelableOnFullDay && params.cancelableMask) {
      params.cancelableMask[idx] = true;
    }
  }
}

/** Applies seasonal blocks for one day. */
export function applySeasonalBlocksToDay(params: {
  space: Asset;
  date: Date;
  dateStr: string;
  slots: GridSlot[];
  slotFractions: number[];
  open: number;
  close: number;
  exceptions: ScheduleException[];
  freeMask: boolean[];
  busyMeta: (OccupancyCellMeta | null)[];
  cancelableMask?: boolean[];
  anonymize?: boolean;
}): void {
  for (const sb of seasonalBlocksForDay(params.space, params.date, params.dateStr)) {
    markRecurringBlockRange({
      slots: params.slots,
      slotFractions: params.slotFractions,
      open: params.open,
      close: params.close,
      startTime: sb.startTime,
      endTime: sb.endTime,
      label: sb.label,
      status: 'SEASONAL',
      dateStr: params.dateStr,
      exceptions: params.exceptions,
      freeMask: params.freeMask,
      busyMeta: params.busyMeta,
      anonymize: params.anonymize,
      cancelableOnFullDay: sb.cancelableOnFullDay,
      cancelableMask: params.cancelableMask,
    });
  }
}

/** Applies calendar-blocking bookings for one day (overrides recurring blocks). */
export function applyBlockingBookingsToDay(params: {
  bookings: Booking[];
  dateStr: string;
  slots: GridSlot[];
  open: number;
  close: number;
  freeMask: boolean[];
  busyMeta: (OccupancyCellMeta | null)[];
  /** Public calendars: do not store guest names or status labels in cell meta. */
  anonymize?: boolean;
}): void {
  const toIdx = (frac: number) => findSlotIndex(params.slots, frac);

  for (const b of params.bookings) {
    if (b.date !== params.dateStr) continue;
    if (!isCalendarBlockingStatus(b.status)) continue;

    const sFrac = Math.max(params.open, Math.min(params.close, parseHour(b.startTime)));
    const eFrac = Math.max(params.open, Math.min(params.close, parseHour(b.endTime)));
    if (eFrac <= sFrac) continue;

    const start = Math.max(0, toIdx(sFrac));
    let end = toIdx(eFrac);
    if (end === -1) end = params.slots.length;

    const status = (b.status ?? '').toUpperCase();
    for (let i = start; i < end; i++) {
      params.freeMask[i] = false;
      params.busyMeta[i] = params.anonymize
        ? { label: '', status: 'OCCUPIED' }
        : { label: b.label || b.guestName || status, status };
    }
  }
}

/** Adds occupied half-hour indices for heatmap (bookings + recurring blocks). */
export function collectOccupiedSlotIndices(input: {
  space: Asset;
  date: Date;
  dateStr: string;
  bookings: Booking[];
  exceptions: ScheduleException[];
  openSlot: number;
  closeSlot: number;
}): Set<number> {
  const { space, date, dateStr, bookings, exceptions, openSlot, closeSlot } = input;
  const occupied = new Set<number>();
  const open = openSlot / 2;
  const close = closeSlot / 2;

  const slots: GridSlot[] = [];
  for (let slot = openSlot; slot < closeSlot; slot++) {
    slots.push({ hour: Math.floor(slot / 2), half: (slot % 2) as 0 | 1 });
  }
  const fracs = slotFractions(slots);
  const freeMask = slots.map(() => true);
  const busyMeta = slots.map(() => null as OccupancyCellMeta | null);

  applyFixedBlocksToDay({
    space, date, dateStr, slots, slotFractions: fracs,
    open, close, exceptions, freeMask, busyMeta,
  });
  applySeasonalBlocksToDay({
    space, date, dateStr, slots, slotFractions: fracs,
    open, close, exceptions, freeMask, busyMeta,
  });
  applyBlockingBookingsToDay({
    bookings, dateStr, slots, open, close, freeMask, busyMeta,
  });

  for (let i = 0; i < freeMask.length; i++) {
    if (!freeMask[i]) occupied.add(openSlot + i);
  }
  return occupied;
}

/**
 * Preview of domingo/festivo recurring blocks for a holiday date.
 * Used in the wizard Calendario step when marking custom/national festivos.
 */
export function computeFestivoBlockPreview(
  space: Pick<Asset, 'weeklyHours' | 'openingTime' | 'closingTime' | 'customHolidays' | 'fixedBlocks' | 'seasonalBlocks' | 'pricing'>,
  dateStr: string,
): FestivoBlockPreview {
  if (!isHolidayDate(dateStr, space)) return { kind: 'not-holiday' };

  const parts = dateStr.split('-').map(Number);
  const date = new Date(parts[0], (parts[1] ?? 1) - 1, parts[2] ?? 1);
  const { open: openStr, close: closeStr } = dayHoursForDate(space as Asset, date, dateStr);
  const open  = parseHour(openStr);
  const close = parseCloseHour(closeStr, openStr);
  if (close <= open) return { kind: 'closed' };

  const slots = buildVisibleSlots(
    space.openingTime ?? '08:00',
    space.closingTime ?? '22:00',
  );
  const fracs = slotFractions(slots);
  const freeMask       = slots.map(() => true);
  const busyMeta       = slots.map(() => null as OccupancyCellMeta | null);
  const cancelableMask = slots.map(() => false);

  applyFixedBlocksToDay({
    space: space as Asset, date, dateStr, slots, slotFractions: fracs,
    open, close, exceptions: [], freeMask, busyMeta, cancelableMask,
  });
  applySeasonalBlocksToDay({
    space: space as Asset, date, dateStr, slots, slotFractions: fracs,
    open, close, exceptions: [], freeMask, busyMeta, cancelableMask,
  });

  const hasBlock = busyMeta.some(m => m?.status === 'FIXED' || m?.status === 'SEASONAL');
  if (!hasBlock) return { kind: 'open-no-blocks' };

  const fdThreshold = space.pricing?.fullDayThreshold ?? DEFAULT_PRICING.fullDayThreshold;
  let fullDayHours    = 0;
  let hasHardOccupied = false;
  for (let si = 0; si < fracs.length; si++) {
    const f = fracs[si];
    if (f < open || f >= close) continue;
    if (freeMask[si] || cancelableMask[si]) fullDayHours += 0.5;
    else hasHardOccupied = true;
  }
  const fullDayEligible = !hasHardOccupied && fullDayHours >= fdThreshold;

  if (hasHardOccupied) return { kind: 'hard-blocks' };
  return { kind: 'cancelable-blocks', fullDayEligible };
}

// ── Day availability status (calendar dot color) ──────────────────────────────
//   available — sin bloqueo                          → verde
//   partial   — alguna hora ocupada, aún cabe reserva → gris
//   full      — 100% cubierto, supera fullDayThreshold,
//               el hueco libre no cabe minHours, o cerrado → rojo
//
// Fuente única para el modal público (`space-calendar-modal`) y el embed
// (`space-embed-calendar`). Maneja festivos/vísperas vía `logicalScheduleDay`,
// y excluye bloques `cancelableOnFullDay` del cómputo "hard".

export type DayAvailabilityStatus = 'available' | 'partial' | 'full';

/** Minimal availability record shape (matches `SpaceAvailabilityRecord`). */
export interface DayAvailabilityRecord {
  startTime?: string;
  endTime?: string;
  status: string;
}

interface MinuteInterval { start: number; end: number; cancelable?: boolean }

const HARD_AVAILABILITY_STATUSES = new Set(['CONFIRMED', 'ACTIVE', 'MANUAL_BLOCK']);
const SOFT_AVAILABILITY_STATUSES = new Set(['PENDING_FORMALIZATION', 'PENDING_VERIFICATION']);

function timeToMinutes(t: string | undefined): number {
  if (!t || !t.includes(':')) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function bookingRecordsToIntervals(
  recs: DayAvailabilityRecord[],
  openMin: number,
  closeMin: number,
): MinuteInterval[] {
  return recs
    .map(r => ({
      start: r.startTime ? Math.max(openMin, timeToMinutes(r.startTime)) : openMin,
      end:   r.endTime   ? Math.min(closeMin, r.endTime === '00:00' ? 1440 : timeToMinutes(r.endTime)) : closeMin,
    }))
    .filter(i => i.end > i.start)
    .sort((a, b) => a.start - b.start);
}

function fixedBlockIntervals(
  space: Asset,
  dateStr: string,
  date: Date,
  exceptions: ScheduleException[],
): MinuteInterval[] {
  const logicalDay = logicalScheduleDay(date, dateStr, space);
  const blocks = (space.fixedBlocks ?? []).filter(b => b.day === logicalDay);
  if (blocks.length === 0) return [];
  let intervals: MinuteInterval[] = blocks.map(b => {
    const start = b.hour * 60 + (b.half === 1 ? 30 : 0);
    return { start, end: start + 30, cancelable: !!b.cancelableOnFullDay };
  });
  const dayExceptions = exceptions.filter(e => e.date === dateStr);
  for (const ex of dayExceptions) {
    const exStart = timeToMinutes(ex.startTime);
    const exEnd   = ex.endTime === '00:00' ? 1440 : timeToMinutes(ex.endTime);
    intervals = intervals.filter(i => i.end <= exStart || i.start >= exEnd);
  }
  return intervals.sort((a, b) => a.start - b.start);
}

function seasonalBlockIntervals(space: Asset, dateStr: string, date: Date): MinuteInterval[] {
  return seasonalBlocksForDay(space, date, dateStr)
    .map(sb => ({
      start: timeToMinutes(sb.startTime),
      end:   sb.endTime === '00:00' ? 1440 : timeToMinutes(sb.endTime),
      cancelable: !!sb.cancelableOnFullDay,
    }))
    .filter(i => i.end > i.start)
    .sort((a, b) => a.start - b.start);
}

function mergedMinutes(sorted: MinuteInterval[]): number {
  if (!sorted.length) return 0;
  let covered = 0;
  let { start, end } = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start <= end) {
      end = Math.max(end, sorted[i].end);
    } else {
      covered += end - start;
      ({ start, end } = sorted[i]);
    }
  }
  return covered + (end - start);
}

function freeIntervals(openMin: number, closeMin: number, blocking: MinuteInterval[]): MinuteInterval[] {
  const sorted = blocking.slice().sort((a, b) => a.start - b.start);
  const merged: MinuteInterval[] = [];
  for (const iv of sorted) {
    const last = merged[merged.length - 1];
    if (last && iv.start <= last.end) {
      last.end = Math.max(last.end, iv.end);
    } else {
      merged.push({ ...iv });
    }
  }
  const free: MinuteInterval[] = [];
  let pos = openMin;
  for (const block of merged) {
    const s = Math.max(block.start, openMin);
    const e = Math.min(block.end, closeMin);
    if (s > pos) free.push({ start: pos, end: s });
    if (e > pos) pos = e;
  }
  if (pos < closeMin) free.push({ start: pos, end: closeMin });
  return free;
}

/**
 * Computes the calendar dot status for a single day, combining the PII-free
 * availability mirror records with the space's recurring `fixedBlocks` /
 * `seasonalBlocks` (one-time `scheduleExceptions` release fixed slots).
 *
 * `availability` may contain all CALENDAR_BLOCKING_STATUSES — the function
 * splits hard (confirmed/active/manual) from soft (pending) internally.
 */
export function computeDayAvailabilityStatus(
  space: Asset,
  dateStr: string,
  availability: DayAvailabilityRecord[],
  exceptions: ScheduleException[],
): DayAvailabilityStatus {
  const date   = dateFromDateStr(dateStr);
  const window = dayOperatingWindowForDate(space, date, dateStr);
  if (window.closed || window.totalMin <= 0) return 'full';

  const { openMin, closeMin, totalMin } = window;

  const clamp = (i: MinuteInterval): MinuteInterval =>
    ({ ...i, start: Math.max(openMin, i.start), end: Math.min(closeMin, i.end) });

  const hardBookingIntervals = bookingRecordsToIntervals(
    availability.filter(r => HARD_AVAILABILITY_STATUSES.has((r.status ?? '').toUpperCase())),
    openMin, closeMin,
  );
  const softIntervals = bookingRecordsToIntervals(
    availability.filter(r => SOFT_AVAILABILITY_STATUSES.has((r.status ?? '').toUpperCase())),
    openMin, closeMin,
  );

  const fixedIntervals    = fixedBlockIntervals(space, dateStr, date, exceptions).map(clamp).filter(i => i.end > i.start);
  const seasonalIntervals = seasonalBlockIntervals(space, dateStr, date).map(clamp).filter(i => i.end > i.start);

  // cancelableOnFullDay blocks no cuentan como "hard" (la reserva los ignora).
  const hardRecurring = [...fixedIntervals, ...seasonalIntervals].filter(i => !i.cancelable);
  const allHard = [...hardBookingIntervals, ...hardRecurring].sort((a, b) => a.start - b.start);

  const hardCovered = mergedMinutes(allHard);
  if (hardCovered >= totalMin) return 'full';

  const fullDayThresholdMin =
    (space.pricing?.fullDayThreshold ?? DEFAULT_PRICING.fullDayThreshold) * 60;
  if (fullDayThresholdMin > 0 && hardCovered >= fullDayThresholdMin) return 'full';

  const allBlocking   = [...allHard, ...softIntervals].sort((a, b) => a.start - b.start);
  const freeBlocks    = freeIntervals(openMin, closeMin, allBlocking);
  const maxFreeMin    = freeBlocks.reduce((m, b) => Math.max(m, b.end - b.start), 0);
  const minBookingMin = minHoursForSpace(space) * 60;
  if (minBookingMin > 0 && maxFreeMin < minBookingMin) return 'full';

  return mergedMinutes(allBlocking) > 0 ? 'partial' : 'available';
}
